/**
 * Role-Based Access Control (RBAC) Utility
 * Implements the 7-role system: SUPERADMIN, PROPERTYADMIN, STAFF, ACCOUNTS, INVENTORY, SERVICE, GUEST
 * Based on RBAC_COMPLETE_GUIDE.md
 */

// Role Definitions with Hierarchy
export const ROLES = {
  SUPERADMIN: 'SUPERADMIN',
  PROPERTYADMIN: 'PROPERTYADMIN',
  STAFF: 'STAFF',
  ACCOUNTS: 'ACCOUNTS',
  INVENTORY: 'INVENTORY',
  SERVICE: 'SERVICE',
  GUEST: 'GUEST'
};

// Role Hierarchy (higher number = more privileges)
export const ROLE_HIERARCHY = {
  [ROLES.SUPERADMIN]: 7,
  [ROLES.PROPERTYADMIN]: 6,
  [ROLES.STAFF]: 5,
  [ROLES.ACCOUNTS]: 4,
  [ROLES.INVENTORY]: 3,
  [ROLES.SERVICE]: 2,
  [ROLES.GUEST]: 1
};

// Permission Definitions
export const PERMISSIONS = {
  // Booking Permissions
  BOOKING_READ: 'BOOKING_READ',
  BOOKING_CREATE: 'BOOKING_CREATE',
  BOOKING_UPDATE: 'BOOKING_UPDATE',
  BOOKING_DELETE: 'BOOKING_DELETE',
  BOOKING_CHECKIN: 'BOOKING_CHECKIN',
  BOOKING_CHECKOUT: 'BOOKING_CHECKOUT',
  
  // Add-on Permissions
  ADDON_READ: 'ADDON_READ',
  ADDON_CREATE: 'ADDON_CREATE',
  ADDON_UPDATE: 'ADDON_UPDATE',
  ADDON_DELETE: 'ADDON_DELETE',
  
  // Inventory Permissions
  INVENTORY_READ: 'INVENTORY_READ',
  INVENTORY_CREATE: 'INVENTORY_CREATE',
  INVENTORY_UPDATE: 'INVENTORY_UPDATE',
  INVENTORY_DELETE: 'INVENTORY_DELETE',
  INVENTORY_ADJUST: 'INVENTORY_ADJUST',
  
  // Payment Permissions
  PAYMENT_READ: 'PAYMENT_READ',
  PAYMENT_CREATE: 'PAYMENT_CREATE',
  PAYMENT_UPDATE: 'PAYMENT_UPDATE',
  PAYMENT_DELETE: 'PAYMENT_DELETE',
  PAYMENT_REFUND: 'PAYMENT_REFUND',
  
  // Report Permissions
  REPORT_READ: 'REPORT_READ',
  REPORT_EXPORT: 'REPORT_EXPORT',
  
  // Availability Block Permissions
  AVAILABILITY_BLOCK_READ: 'AVAILABILITY_BLOCK_READ',
  AVAILABILITY_BLOCK_CREATE: 'AVAILABILITY_BLOCK_CREATE',
  AVAILABILITY_BLOCK_UPDATE: 'AVAILABILITY_BLOCK_UPDATE',
  AVAILABILITY_BLOCK_DELETE: 'AVAILABILITY_BLOCK_DELETE',
  
  // Room Permissions
  ROOM_READ: 'ROOM_READ',
  ROOM_CREATE: 'ROOM_CREATE',
  ROOM_UPDATE: 'ROOM_UPDATE',
  ROOM_DELETE: 'ROOM_DELETE',
  
  // Course Permissions
  COURSE_READ: 'COURSE_READ',
  COURSE_CREATE: 'COURSE_CREATE',
  COURSE_UPDATE: 'COURSE_UPDATE',
  COURSE_DELETE: 'COURSE_DELETE',
  
  // User Management Permissions
  USER_READ: 'USER_READ',
  USER_CREATE: 'USER_CREATE',
  USER_UPDATE: 'USER_UPDATE',
  USER_DELETE: 'USER_DELETE',
  
  // Audit Permissions
  AUDIT_READ: 'AUDIT_READ',
  
  // Property Permissions
  PROPERTY_READ: 'PROPERTY_READ',
  PROPERTY_CREATE: 'PROPERTY_CREATE',
  PROPERTY_UPDATE: 'PROPERTY_UPDATE',
  PROPERTY_DELETE: 'PROPERTY_DELETE'
};

// Role Permission Mapping
export const ROLE_PERMISSIONS = {
  [ROLES.SUPERADMIN]: [
    // All permissions
    ...Object.values(PERMISSIONS)
  ],
  
  [ROLES.PROPERTYADMIN]: [
    // Bookings
    PERMISSIONS.BOOKING_READ,
    PERMISSIONS.BOOKING_CREATE,
    PERMISSIONS.BOOKING_UPDATE,
    PERMISSIONS.BOOKING_DELETE,
    PERMISSIONS.BOOKING_CHECKIN,
    PERMISSIONS.BOOKING_CHECKOUT,
    
    // Add-ons
    PERMISSIONS.ADDON_READ,
    PERMISSIONS.ADDON_CREATE,
    PERMISSIONS.ADDON_UPDATE,
    PERMISSIONS.ADDON_DELETE,
    
    // Inventory
    PERMISSIONS.INVENTORY_READ,
    PERMISSIONS.INVENTORY_CREATE,
    PERMISSIONS.INVENTORY_UPDATE,
    PERMISSIONS.INVENTORY_DELETE,
    PERMISSIONS.INVENTORY_ADJUST,
    
    // Payments
    PERMISSIONS.PAYMENT_READ,
    PERMISSIONS.PAYMENT_CREATE,
    PERMISSIONS.PAYMENT_UPDATE,
    PERMISSIONS.PAYMENT_DELETE,
    PERMISSIONS.PAYMENT_REFUND,
    
    // Reports
    PERMISSIONS.REPORT_READ,
    PERMISSIONS.REPORT_EXPORT,
    
    // Availability Blocks
    PERMISSIONS.AVAILABILITY_BLOCK_READ,
    PERMISSIONS.AVAILABILITY_BLOCK_CREATE,
    PERMISSIONS.AVAILABILITY_BLOCK_UPDATE,
    PERMISSIONS.AVAILABILITY_BLOCK_DELETE,
    
    // Rooms
    PERMISSIONS.ROOM_READ,
    PERMISSIONS.ROOM_CREATE,
    PERMISSIONS.ROOM_UPDATE,
    PERMISSIONS.ROOM_DELETE,
    
    // Courses
    PERMISSIONS.COURSE_READ,
    PERMISSIONS.COURSE_CREATE,
    PERMISSIONS.COURSE_UPDATE,
    PERMISSIONS.COURSE_DELETE,
    
    // Users
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_UPDATE,
    
    // Audit
    PERMISSIONS.AUDIT_READ,
    
    // Property
    PERMISSIONS.PROPERTY_READ,
    PERMISSIONS.PROPERTY_CREATE,
    PERMISSIONS.PROPERTY_UPDATE
  ],
  
  [ROLES.STAFF]: [
    // Bookings
    PERMISSIONS.BOOKING_READ,
    PERMISSIONS.BOOKING_CREATE,
    PERMISSIONS.BOOKING_UPDATE,
    PERMISSIONS.BOOKING_CHECKIN,
    PERMISSIONS.BOOKING_CHECKOUT,
    
    // Add-ons
    PERMISSIONS.ADDON_READ,
    PERMISSIONS.ADDON_CREATE,
    PERMISSIONS.ADDON_UPDATE,
    
    // Inventory
    PERMISSIONS.INVENTORY_READ,
    PERMISSIONS.INVENTORY_UPDATE,
    PERMISSIONS.INVENTORY_ADJUST,
    
    // Payments
    PERMISSIONS.PAYMENT_READ,
    PERMISSIONS.PAYMENT_CREATE,
    
    // Availability Blocks
    PERMISSIONS.AVAILABILITY_BLOCK_READ,
    PERMISSIONS.AVAILABILITY_BLOCK_CREATE,
    PERMISSIONS.AVAILABILITY_BLOCK_UPDATE,
    
    // Rooms
    PERMISSIONS.ROOM_READ,
    
    // Courses
    PERMISSIONS.COURSE_READ,
    
    // Reports
    PERMISSIONS.REPORT_READ,
    
    // Users
    PERMISSIONS.USER_READ,
    
    // Property
    PERMISSIONS.PROPERTY_READ
  ],
  
  [ROLES.ACCOUNTS]: [
    // Bookings (Read-only for financial analysis)
    PERMISSIONS.BOOKING_READ,
    
    // Add-ons (Read-only)
    PERMISSIONS.ADDON_READ,
    
    // Payments (Read-only)
    PERMISSIONS.PAYMENT_READ,
    
    // Reports (Full access)
    PERMISSIONS.REPORT_READ,
    PERMISSIONS.REPORT_EXPORT,
    
    // Rooms (Read-only)
    PERMISSIONS.ROOM_READ,
    
    // Courses (Read-only)
    PERMISSIONS.COURSE_READ,
    
    // Users (Read-only)
    PERMISSIONS.USER_READ,
    
    // Property (Read-only)
    PERMISSIONS.PROPERTY_READ
  ],
  
  [ROLES.INVENTORY]: [
    // Bookings (Read-only to check service usage)
    PERMISSIONS.BOOKING_READ,
    
    // Add-ons (Full CRUD)
    PERMISSIONS.ADDON_READ,
    PERMISSIONS.ADDON_CREATE,
    PERMISSIONS.ADDON_UPDATE,
    PERMISSIONS.ADDON_DELETE,
    
    // Inventory (Full CRUD + Adjust)
    PERMISSIONS.INVENTORY_READ,
    PERMISSIONS.INVENTORY_CREATE,
    PERMISSIONS.INVENTORY_UPDATE,
    PERMISSIONS.INVENTORY_DELETE,
    PERMISSIONS.INVENTORY_ADJUST,
    
    // Rooms (Read-only)
    PERMISSIONS.ROOM_READ,
    
    // Courses (Read-only)
    PERMISSIONS.COURSE_READ,
    
    // Users (Read-only)
    PERMISSIONS.USER_READ,
    
    // Property (Read-only)
    PERMISSIONS.PROPERTY_READ
  ],
  
  [ROLES.SERVICE]: [
    // Bookings (Read-only for service planning)
    PERMISSIONS.BOOKING_READ,
    
    // Add-ons (Read and Create)
    PERMISSIONS.ADDON_READ,
    PERMISSIONS.ADDON_CREATE,
    
    // Inventory (Read-only)
    PERMISSIONS.INVENTORY_READ,
    
    // Rooms (Read-only)
    PERMISSIONS.ROOM_READ,
    
    // Courses (Read-only)
    PERMISSIONS.COURSE_READ,
    
    // Users (Read-only)
    PERMISSIONS.USER_READ,
    
    // Property (Read-only)
    PERMISSIONS.PROPERTY_READ
  ],
  
  [ROLES.GUEST]: [
    // Bookings (Own bookings only)
    PERMISSIONS.BOOKING_READ
  ]
};

/**
 * Check if a role has a specific permission
 * @param {string} role - User role
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (role, permission) => {
  if (!role || !permission) return false;
  const rolePermissions = ROLE_PERMISSIONS[role] || [];
  return rolePermissions.includes(permission);
};

/**
 * Check if a role has all of the specified permissions
 * @param {string} role - User role
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAllPermissions = (role, permissions) => {
  if (!role || !permissions || permissions.length === 0) return false;
  return permissions.every(permission => hasPermission(role, permission));
};

/**
 * Check if a role has any of the specified permissions
 * @param {string} role - User role
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAnyPermission = (role, permissions) => {
  if (!role || !permissions || permissions.length === 0) return false;
  return permissions.some(permission => hasPermission(role, permission));
};

/**
 * Check if a user has one of the specified roles
 * @param {string} userRole - User's role
 * @param {string[]} allowedRoles - Array of allowed roles
 * @returns {boolean}
 */
export const hasRole = (userRole, allowedRoles) => {
  if (!userRole || !allowedRoles || allowedRoles.length === 0) return false;
  return allowedRoles.includes(userRole);
};

/**
 * Get all permissions for a role
 * @param {string} role - User role
 * @returns {string[]}
 */
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || [];
};

/**
 * Check if user can access admin dashboard
 * @param {string} role - User role
 * @returns {boolean}
 */
export const canAccessAdminDashboard = (role) => {
  return hasRole(role, [
    ROLES.SUPERADMIN,
    ROLES.PROPERTYADMIN,
    ROLES.STAFF,
    ROLES.ACCOUNTS,
    ROLES.INVENTORY,
    ROLES.SERVICE
  ]);
};

/**
 * Get role display information
 * @param {string} role - User role
 * @returns {object}
 */
export const getRoleInfo = (role) => {
  const roleInfo = {
    [ROLES.SUPERADMIN]: {
      label: 'Super Administrator',
      color: '#8E3628',
      description: 'Full system access'
    },
    [ROLES.PROPERTYADMIN]: {
      label: 'Property Administrator',
      color: '#163626',
      description: 'Property management'
    },
    [ROLES.STAFF]: {
      label: 'Staff',
      color: '#163626',
      description: 'Daily operations'
    },
    [ROLES.ACCOUNTS]: {
      label: 'Accounts',
      color: '#975534',
      description: 'Financial reporting'
    },
    [ROLES.INVENTORY]: {
      label: 'Inventory Manager',
      color: '#975534',
      description: 'Inventory management'
    },
    [ROLES.SERVICE]: {
      label: 'Service Coordinator',
      color: '#163626',
      description: 'Service management'
    },
    [ROLES.GUEST]: {
      label: 'Guest',
      color: '#5F6B61',
      description: 'Booking access'
    }
  };
  
  return roleInfo[role] || {
    label: role,
    color: '#5F6B61',
    description: 'Unknown role'
  };
};

/**
 * Check if role A can manage role B
 * @param {string} roleA - Managing role
 * @param {string} roleB - Role to be managed
 * @returns {boolean}
 */
export const canManageRole = (roleA, roleB) => {
  // SUPERADMIN can manage all roles
  if (roleA === ROLES.SUPERADMIN) return true;
  
  // PROPERTYADMIN can manage all except SUPERADMIN
  if (roleA === ROLES.PROPERTYADMIN && roleB !== ROLES.SUPERADMIN) return true;
  
  // Others cannot manage users
  return false;
};

/**
 * Get available roles that a user can create/modify
 * @param {string} currentUserRole - Current user's role
 * @returns {string[]}
 */
export const getManageableRoles = (currentUserRole) => {
  if (currentUserRole === ROLES.SUPERADMIN) {
    return Object.values(ROLES);
  }
  
  if (currentUserRole === ROLES.PROPERTYADMIN) {
    return [
      ROLES.PROPERTYADMIN,
      ROLES.STAFF,
      ROLES.ACCOUNTS,
      ROLES.INVENTORY,
      ROLES.SERVICE,
      ROLES.GUEST
    ];
  }
  
  return [];
};

/**
 * Check if role A can delete role B
 * Users can only delete accounts with LOWER access levels than their own
 * @param {string} roleA - Deleting role
 * @param {string} roleB - Role to be deleted
 * @returns {boolean}
 */
export const canDeleteRole = (roleA, roleB) => {
  // Cannot delete equal or higher roles
  if (roleA === roleB) return false;
  
  // SUPERADMIN can delete PROPERTYADMIN and below (but not other SUPERADMINs)
  if (roleA === ROLES.SUPERADMIN) {
    return roleB !== ROLES.SUPERADMIN;
  }
  
  // PROPERTYADMIN can delete STAFF, ACCOUNTS, INVENTORY, SERVICE, GUEST
  // (but not SUPERADMIN or other PROPERTYADMINs)
  if (roleA === ROLES.PROPERTYADMIN) {
    return roleB !== ROLES.SUPERADMIN && roleB !== ROLES.PROPERTYADMIN;
  }
  
  // Others cannot delete users
  return false;
};

// Export default object with all utilities
export default {
  ROLES,
  ROLE_HIERARCHY,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  hasPermission,
  hasAllPermissions,
  hasAnyPermission,
  hasRole,
  getRolePermissions,
  canAccessAdminDashboard,
  getRoleInfo,
  canManageRole,
  getManageableRoles,
  canDeleteRole
};
